import { useNavigate } from "react-router-dom";

interface AssetOverviewProps {
  totalAssets: number; totalReturnRate: number; todayProfit: number;
  floatingProfit: number; netValueHistory: number[];
}

function formatMoney(v: number): string {
  if (Math.abs(v) >= 10000) return (v / 10000).toFixed(2) + "万";
  return v.toFixed(2);
}
const signed = (v: number) => (v >= 0 ? "+" : "-");
const pnlColor = (v: number) => (v >= 0 ? "#E11D48" : "#10B981");

function MiniChart({ data }: { data: number[] }) {
  const min = Math.min(...data); const max = Math.max(...data);
  const range = max - min || 1; const h = 30;
  const w = data.length * 14;
  const points = data.map((v, i) => `${i * 14},${h - ((v - min) / range) * (h - 4) - 2}`).join(" ");
  const up = data[data.length - 1] >= data[0];
  const color = up ? "#E11D48" : "#10B981";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
      <defs><linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.25" /><stop offset="100%" stopColor={color} stopOpacity="0.02" /></linearGradient></defs>
      <polygon points={`0,${h} ${points} ${w},${h}`} fill="url(#chartGrad)" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function AssetOverview({ totalAssets, totalReturnRate, todayProfit, floatingProfit, netValueHistory }: AssetOverviewProps) {
  const navigate = useNavigate();
  return (
    <div className="px-4 mt-1">
      <div
        className="bg-white rounded-2xl p-4 relative overflow-hidden cursor-pointer active:scale-[0.99] transition-transform"
        onClick={() => navigate("/reports")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && navigate("/reports")}
      >
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-primary-500 via-accent-500 to-transparent" />
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground-500 tracking-wide">模拟总资产</span>
            <i className="ri-arrow-right-up-line text-[10px] text-foreground-300" />
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={(e) => { e.stopPropagation(); navigate("/positions"); }} className="flex items-center gap-1 text-[10px] text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full cursor-pointer hover:bg-primary-100 transition-colors">
              <i className="ri-funds-line text-xs" />持仓快照
            </button>
            <span className="text-[10px] text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">模拟</span>
          </div>
        </div>
        <div className="flex items-end justify-between gap-3">
          <div className="flex-shrink-0">
            <div className="text-[32px] font-bold text-foreground-950 tracking-tight leading-none">¥{formatMoney(totalAssets)}</div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-xs font-semibold" style={{ color: pnlColor(totalReturnRate) }}>{signed(totalReturnRate)}{Math.abs(totalReturnRate)}%</span>
              <span className="text-[10px] text-foreground-400">累计收益</span>
            </div>
          </div>
          <div className="flex-1 h-[42px] min-w-0"><MiniChart data={netValueHistory} /></div>
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t border-background-100">
          <div className="flex-1 bg-background-50 rounded-xl p-2.5 text-center">
            <div className="text-[11px] font-semibold" style={{ color: pnlColor(totalReturnRate) }}>{signed(totalReturnRate)}{Math.abs(totalReturnRate)}%</div>
            <div className="text-[10px] text-foreground-400 mt-0.5">累计收益率</div>
          </div>
          <div className="flex-1 bg-background-50 rounded-xl p-2.5 text-center">
            <div className="text-[11px] font-semibold" style={{ color: pnlColor(todayProfit) }}>{signed(todayProfit)}¥{formatMoney(Math.abs(todayProfit))}</div>
            <div className="text-[10px] text-foreground-400 mt-0.5">今日盈亏</div>
          </div>
          <div className="flex-1 bg-background-50 rounded-xl p-2.5 text-center">
            <div className="text-[11px] font-semibold" style={{ color: pnlColor(floatingProfit) }}>{signed(floatingProfit)}¥{formatMoney(Math.abs(floatingProfit))}</div>
            <div className="text-[10px] text-foreground-400 mt-0.5">浮动盈亏</div>
          </div>
        </div>
      </div>
    </div>
  );
}
